import { useState, useEffect } from 'react';
import { todayInputValue, formatNumber } from '../../../../utils/format';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { showToast } from '../../../../components/base/Toast';
import { stockOutSupplyItem, issueSupplyToFarmer, getFarmersByHtx, getSeasonsByHtx } from '../../../../store';
import { useAuth } from '../../../../hooks/useAuth';
import type { SupplyItem } from '../../../../types';

interface OutStockModalProps {
  item: SupplyItem | null;
  onClose: () => void;
}

const HINH_THUC_LIST = ['Tạm ứng', 'Bán', 'Cho thuê', 'Cấp phát', 'Thu hồi', 'Khác'];

export function OutStockModal({ item, onClose }: OutStockModalProps) {
  useModalBackHandler(true, onClose);
  const { user } = useAuth();
  const [form, setForm] = useState({ soLuong: '', nongHoId: '', muaVuId: '', ngay: todayInputValue(), hinhThuc: 'Tạm ứng' as 'Tạm ứng' | 'Bán' | 'Cho thuê' | 'Cấp phát' | 'Thu hồi' | 'Khác', lyDo: '', ghiChu: '' });
  const [saving, setSaving] = useState(false);
  const farmers = getFarmersByHtx(user?.htxId);
  const seasons = getSeasonsByHtx(user?.htxId);

  useEffect(() => {
    if (item) {
      setForm({ soLuong: '', nongHoId: '', muaVuId: '', ngay: todayInputValue(), hinhThuc: 'Tạm ứng', lyDo: '', ghiChu: '' });
    }
  }, [item]);

  if (!item) return null;

  const soLuongNum = Number(form.soLuong) || 0;
  const isOverAvailable = soLuongNum > item.available;
  const isPartial = soLuongNum > 0 && soLuongNum < item.available;
  const canSave = soLuongNum > 0 && !isOverAvailable && !!form.ngay && !saving;

  const handleSave = async () => {
    if (!soLuongNum || soLuongNum <= 0) { showToast('Số lượng xuất phải lớn hơn 0', 'warning'); return; }
    if (soLuongNum > item.available) { showToast(`Không đủ hàng! Chỉ còn ${item.available} ${item.unit} khả dụng`, 'error'); return; }
    if (!form.ngay) { showToast('Vui lòng chọn ngày xuất', 'warning'); return; }
    if (!user?.htxId) return;

    setSaving(true);
    try {
      if (form.nongHoId) {
        // Issuing to a farmer is one atomic backend action — it decrements
        // this SupplyItem's stock AND creates the farmer's Material record
        // server-side (see store/supply.ts's issueSupplyToFarmer).
        await issueSupplyToFarmer(item.id, form.nongHoId, soLuongNum, user.htxId, form.muaVuId || undefined, form.ghiChu || `Xuất từ kho HTX - ${form.hinhThuc}`);
      } else {
        await stockOutSupplyItem(item.id, soLuongNum, form.lyDo || `Xuất kho ${item.name}`, user.htxId);
      }
      showToast(`Xuất kho thành công: -${soLuongNum} ${item.unit}${form.nongHoId ? ` cho ${farmers.find((f) => f.id === form.nongHoId)?.name || ''}` : ''}`, 'success');
      onClose();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Xuất kho thất bại', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Xuất kho</h3>
            <p className="text-xs text-slate-400">{item.maVatTu} · {item.name}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400"><i className="ri-close-line text-lg" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {/* Tồn kho hiện tại */}
          <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Tồn kho</span>
              <span className="text-sm font-bold text-slate-900">{formatNumber(item.stock)} {item.unit}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Khả dụng</span>
              <span className={`text-sm font-bold ${item.available > 0 ? 'text-agrida-700' : 'text-red-600'}`}>{formatNumber(item.available)} {item.unit}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Đã giữ chỗ</span>
              <span className="text-sm font-bold text-amber-700">{formatNumber(item.reserved)} {item.unit}</span>
            </div>
          </div>

          {/* Số lượng xuất */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Số lượng xuất <span className="text-red-500">*</span></label>
            <div className="flex items-center gap-2">
              <input type="number" value={form.soLuong} onChange={(e) => setForm({ ...form, soLuong: e.target.value })} className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder={`Tối đa ${item.available}`} />
              <span className="text-sm text-slate-400">{item.unit}</span>
            </div>
            {isOverAvailable && (
              <div className="mt-1.5 flex items-center gap-1.5 text-red-600 text-xs bg-red-50 rounded-md px-2.5 py-1.5">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-error-warning-line" /></span>
                Vượt quá tồn kho khả dụng ({item.available} {item.unit})! Vui lòng giảm số lượng hoặc chọn cấp một phần.
              </div>
            )}
            {item.available > 0 && (
              <div className="flex gap-1.5 mt-1.5">
                <button type="button" onClick={() => setForm({ ...form, soLuong: String(Math.ceil(item.available * 0.5)) })} className="text-[10px] px-2 py-1 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors whitespace-nowrap">50%</button>
                <button type="button" onClick={() => setForm({ ...form, soLuong: String(item.available) })} className="text-[10px] px-2 py-1 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors whitespace-nowrap">100% ({item.available})</button>
              </div>
            )}
          </div>

          {/* Nông hộ */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nông hộ nhận</label>
            <select value={form.nongHoId} onChange={(e) => setForm({ ...form, nongHoId: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
              <option value="">— Không chọn —</option>
              {farmers.map((f) => {
                const suffix = f.code || f.phone;
                return <option key={f.id} value={f.id}>{suffix ? `${f.name} (${suffix})` : f.name}</option>;
              })}
            </select>
            {form.nongHoId && (
              <p className="text-xs text-sky-600 mt-1">Vật tư sẽ được tự động cập nhật vào kho của nông hộ này.</p>
            )}
          </div>

          {/* Mùa vụ */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mùa vụ (nếu có)</label>
            <select value={form.muaVuId} onChange={(e) => setForm({ ...form, muaVuId: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
              <option value="">— Không chọn —</option>
              {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          {/* Hình thức */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Hình thức cung ứng</label>
            <select value={form.hinhThuc} onChange={(e) => setForm({ ...form, hinhThuc: e.target.value as typeof form.hinhThuc })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
              {HINH_THUC_LIST.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            {form.hinhThuc === 'Cho thuê' && (
              <div className="mt-1.5 flex items-center gap-1.5 text-amber-600 text-xs bg-amber-50 rounded-md px-2.5 py-1.5">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-information-line" /></span>
                Vật tư cho thuê sẽ được theo dõi riêng, không tạo công nợ tiền.
              </div>
            )}
          </div>

          {/* Ngày xuất */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ngày xuất <span className="text-red-500">*</span></label>
            <input type="date" value={form.ngay} onChange={(e) => setForm({ ...form, ngay: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
          </div>

          {/* Lý do */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Lý do xuất</label>
            <input type="text" value={form.lyDo} onChange={(e) => setForm({ ...form, lyDo: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Lý do xuất kho" />
          </div>

          {/* Ghi chú */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú</label>
            <textarea value={form.ghiChu} onChange={(e) => setForm({ ...form, ghiChu: e.target.value })} maxLength={500} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Ghi chú..." />
          </div>

          {/* Preview */}
          {soLuongNum > 0 && !isOverAvailable && (
            <div className="bg-amber-50 rounded-xl p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-amber-700">Tồn sau xuất</span>
                <span className="font-semibold text-amber-800">{formatNumber(item.stock - soLuongNum)} {item.unit}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-amber-700">Khả dụng sau xuất</span>
                <span className="font-semibold text-amber-800">{formatNumber(item.available - soLuongNum)} {item.unit}</span>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]">Hủy</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className={`px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-colors whitespace-nowrap min-h-[44px] ${canSave ? (isPartial ? 'bg-amber-600 hover:bg-amber-700' : 'bg-agrida-700 hover:bg-agrida-800') : 'bg-slate-300 cursor-not-allowed'}`}
          >
            {isPartial ? `Xuất một phần (${soLuongNum} ${item.unit})` : 'Xuất toàn bộ'}
          </button>
        </div>
      </div>
    </div>
  );
}

